<template>
  <div class="modal is-active" @keydown.esc="close()" tabindex="0">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title" v-if="applications.length > 1">Bulk Email</p>
        <p v-if="applications.length === 1" class="modal-card-title">Email for {{ applications[0].organization.name }}</p>
      </header>

      <section class="modal-card-body">
        <div v-if="!loading">
          <div v-if="step === 1" class="content">
            <div class="checkbox-row" v-for="template in templates" :key="template.id">
              <input type="radio" class="radio" :value="template" v-model="selectedTemplate" />
              <p style="marginLeft: 1rem;">{{ template.subject }}</p>
            </div>
          </div>
          <div v-if="step === 2" class="content">
            <p class="is-italic">You are viewing a preview for application: {{ applications[0].organization.name }}</p>
            <div v-html="preview"></div>
          </div>
          <div v-if="step === 3" class="content">
            <p class="is-italic">
              Please review the list of applications whose submitters will be emailed. Hit 'Submit' at the bottom to send the emails.
            </p>
            <table class="table is-fullwidth" style="marginTop: 1rem;">
              <thead>
                <tr>
                  <th>Send Bulk Email</th>
                  <th>Org Name</th>
                  <th>Email Already Sent?</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="application in applications" :key="application.id">
                  <td>
                    <input
                      type="checkbox"
                      class="checkbox"
                      :value="application"
                      :checked="selectedAppIDs.indexOf(application.id) > -1"
                      v-model="selectedApplications"
                    />
                  </td>
                  <td>
                    <router-link
                      :to="{ name: 'StaffApplicationReviewDetail', params: { applicationId: application.id } }"
                    >
                      {{ application.organization.name }}
                    </router-link>
                  </td>
                  <td>
                    <i class="fa fa-check" v-if="alreadyEmailed(application)"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="step === 4" class="content">
            <p class="is-italic">You are sending a duplicate email to a submitter. Are you sure you want to?</p>
          </div>
        </div>
        <LoadingBox v-if="loading" />
      </section>
      <footer class="modal-card-foot">
        <button class="button" @click="close()">Cancel</button>
        <button class="button" @click="previous()" v-if="step !== 1">Previous</button>
        <button v-if="step < 3" class="button is-primary" :disabled="disabled" @click="next()">
          Next
        </button>
        <button v-if="step === 3" class="button is-primary" @click="checkIfSentAlready()">Submit</button>
        <button v-if="step === 4" class="button is-primary" @click="sendEmails()">I'm Fine. Send it</button>
        <p class="is-italic" style="marginLeft: 1rem;" v-if="step === 1">
          A preview of the email will be shown on the next screen
        </p>
        <p class="has-text-danger" v-if="invalidApplications.length && step === 3">
          WARNING: You have selected submitters that have already been sent this email. 
          Please confirm this is intended before proceeding.
        </p>
      </footer>
    </div>
  </div>
</template>

<script>
import EmailTemplate from '@/services/email-templates'

import LoadingBox from '@/components/LoadingBox'

export default {
  name: 'BulkEmailSubmittersModal',
  components: {
    LoadingBox,
  },
  props: {
    applications: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      templates: [],
      selectedApplications: [],
      selectedTemplate: null,
      preview: null,
      step: 1,
      loading: false,
    }
  },
  computed: {
    disabled() {
      return !this.selectedTemplate
    },
    invalidApplications() {
      return this.selectedApplications.filter(app => this.alreadyEmailed(app))
    },
    selectedAppIDs() {
      return this.selectedApplications.map(a => a.id)
    },
  },
  watch: {
    // When the user selects a template, auto-select all applications who have not been sent an email from that template.
    selectedTemplate() {
      this.selectedApplications = this.applications.filter(app => !this.alreadyEmailed(app))
    },
  },
  async created() {
    await this.fetchTemplates()
  },
  methods: {
    alreadyEmailed(application) {
      return !!application?.submitter?.emailRecords?.find(
        record => record.template === this.selectedTemplate.id,
      )
    },
    async fetchTemplates() {
      try {
        const response = await EmailTemplate.api.list()
        this.templates = response.results
      } catch (err) {
        this.$Alert.alert({
          type: 'error',
          message: `<p>Error Retrieving Email Templates</p>`,
          timeout: 6000,
        })
      }
    },
    async getEmailPreview() {
      const template = this.selectedTemplate
      const app = this.applications[0]
      const response = await EmailTemplate.api.preview(template.id, app.submitter.id)
      this.preview = response
    },
    next() {
      this.step = this.step + 1
      if (this.step === 2) {
        this.getEmailPreview()
      }
      return
    },
    previous() {
      this.step = this.step - 1
    },
    close() {
      this.$emit('close')
    },
    checkIfSentAlready(){
      if (this.invalidApplications && this.invalidApplications.length) {
        this.step = this.step + 1
      } else {
        this.sendEmails()
      }
    },
    async sendEmails() {
      this.loading = true
      try {
        const response = await EmailTemplate.api.bulkEmailTemplate(
          this.selectedTemplate.id,
          this.selectedApplications.map(a => a.submitter.id),
        )
        //console.log(response)
        this.$Alert.alert({
          type: 'info',
          message: `<p>${this.selectedApplications.length} Submission Group Emailed Template: ${
            this.selectedTemplate.name
          }</p>`,
          timeout: 6000,
        })
        this.$emit('submit')
        this.close()
      } catch (err) {
        const error = err.response.data['detail']
        this.$Alert.alert({
          type: 'error',
          message: `<p>Error Sending Emails: ${error}</p>`,
          timeout: 6000,
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-card {
  width: 65vw;
}
.checkbox-row {
  display: flex;
  margin-bottom: 0.5rem;
}
</style>
